import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerDaek() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Syn og udstyr", path: "/trailer-syn" },
    { name: "Dæk til trailer", path: "/trailer-daek" }
  ];
  const tocItems = [
    { id: "lovkrav-moensterdybde", title: "Lovkrav til dækmønster (1,6 mm)" },
    { id: "alder-og-dot-maerkning", title: "Dækalder, krakelering og 6-års reglen for Tempo 100" },
    { id: "daektryk-og-tabeller", title: "Korrekt dæktryk for trailerdæk" },
    { id: "stoerrelser-og-c-daek", title: "Typiske dækstørrelser (145/80 R13) og C-dæk" },
    { id: "baadtrailere-og-vinterdaek", title: "Bådtrailere, stilstand og vinterdæk" }
  ];

  const faq = [
    {
      q: "Hvor meget mønster skal der være på trailerdæk?",
      a: "Lovkravet til mønsterdybde på en trailer er mindst 1,6 mm i hovedmønsterets riller (samme regel som for personbiler). Da trailere sjældent slides ned på mønsteret, men derimod ældes i gummiet, anbefales det dog at skifte dæk, hvis de er mere end 6–8 år gamle, eller hvis der opstår revner."
    },
    {
      q: "Hvor gamle må dæk være på en trailer?",
      a: "Ved almindelig kørsel (80 km/t) er der ingen fast lovgrænse for dækkenes alder, så længe de er fri for synlige skader, revner og krakeleringer. Hvis traileren er godkendt til dansk eller tysk Tempo 100, er der derimod et strengt lovkrav om, at dækkene højst må være 6 år gamle regnet fra produktionsugen på DOT-mærket."
    },
    {
      q: "Hvor højt dæktryk skal en trailer have?",
      a: "Trailerdæk skal generelt have et væsentligt højere tryk end personbilsdæk. Standarddæk (f.eks. 145/80 R13) skal typisk have mellem 2,5 og 3,0 bar (36–44 PSI). Forstærkede varevognsdæk (C-dæk) på tungere trailere kræver ofte mellem 3,8 og 4,5 bar (55–65 PSI) for at bære den fulde last."
    },
    {
      q: "Hvad betyder 'C' i en dækstørrelse som 155 R13 C?",
      a: "'C' står for Commercial (erhverv/varevogn). Det angiver, at dækkets karkasse er forstærket med flere lag (ofte 6 eller 8 ply rating) og kan tåle et væsentligt højere dæktryk og en større vægtbelastning (højere Load Index) end et almindeligt personbilsdæk."
    }
  ];

  const sources = [
    { title: "Bekendtgørelse om køretøjers indretning og udstyr (BEK nr. 154 af 28/01/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/154" },
    { title: "Færdselsstyrelsen: Krav og regler for Tempo 100 påhængskøretøjer", source: "fstyr.dk", url: "https://fstyr.dk/privat/krav-til-koeretoejer/tempo-100" },
    { title: "Bekendtgørelse om godkendelse og syn af køretøjer (BEK nr. 1429 af 29/11/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1429" }
  ];

  return (
    <>
      <SEO 
        title="Trailer dæk: Størrelser, dæktryk, alder og regler" 
        description="Alt om dæk til trailer: minimum mønsterdybde (1,6 mm), 6-års reglen for Tempo 100, korrekt dæktryk (bar/PSI) og forstærkede C-dæk." 
        pathname="/trailer-daek"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Trailerdæk: Regler for mønster, dæktryk og alder
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Ifølge færdselsloven skal dæk på en trailer have <strong>mindst 1,6 mm mønsterdybde</strong>. Dækkene må ikke have synlige krakeleringer, revner eller skader på dæksiderne.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Kører du med en trailer godkendt til Tempo 100, gælder der et absolut krav om, at dækkene <strong>højst må være 6 år gamle</strong>. Derudover kræver trailerdæk væsentligt højere dæktryk end almindelige personbilsdæk for at forhindre varmeudvikling og punkteringer under belastning.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="De 3 vigtigste regler for trailerdæk">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Mønsterdybde: <strong>Mindst 1,6 mm</strong> (lovkrav) – anbefalet 3,0 mm.</li>
            <li>• Tempo 100 dækalder: <strong>Maks. 6 år</strong> fra produktionsdato (DOT).</li>
            <li>• Dæktryk: <strong>2,5 – 4,5 bar</strong> afhængigt af dæktype og totalvægt.</li>
          </ul>
        </Callout>

        <h2 id="lovkrav-moensterdybde" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Lovkrav til dækmønster (1,6 mm)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Præcis som på en personbil er lovkravet til mønsterdybde på en trailer minimum <strong>1,6 mm</strong> i de midterste 75 % af slidbanens bredde hele vejen rundt om hjulet.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          I praksis er det dog yderst sjældent, at et trailerdæk slides ned til 1,6 mm, medmindre traileren kører tusindvis af kilometer årligt i erhvervsmæssig drift. For langt de fleste have- og fritidstrailere er det derimod <strong>sollys, ozon og stilstand</strong>, der ødelægger dækket, længe før mønsteret er slidt op.
        </p>

        <h2 id="alder-og-dot-maerkning" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Dækalder, krakelering og 6-års reglen for Tempo 100
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          På dækkets side findes en oval mærkning med fire tal, kendt som <strong>DOT-mærket</strong>:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>De to første cifre angiver <strong>produktionsugen</strong>.</li>
          <li>De to sidste cifre angiver <strong>produktionsåret</strong>.</li>
          <li>Eksempel: <code>2420</code> betyder, at dækket er produceret i uge 24 i år 2020.</li>
        </ul>

        <div className="my-6 p-4 border border-[#E4E1D8] bg-[#FBFAF6] rounded-[4px] space-y-3">
          <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
            6-års grænsen ved Tempo 100
          </h3>
          <p className="text-[15px] text-[#1A1C1F] m-0">
            For at en trailer må køre 100 km/t under den danske eller tyske Tempo 100-ordning, må dækkene ikke være ældre end 6 år. Overskrides 6-års datoen, er Tempo 100-godkendelsen øjeblikkeligt ugyldig, og traileren må kun fremføres med den generelle hastighedsgrænse på 80 km/t. Læs mere i guiden om <Link to="/tempo-100" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">Tempo 100 regler</Link>.
          </p>
        </div>

        <h2 id="daektryk-og-tabeller" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Korrekt dæktryk for trailerdæk
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Et forkert dæktryk er den hyppigste årsag til eksploderede trailerdæk på motorvejen. Når en fuldt læsset trailer kører med for lavt lufttryk, opstår der voldsom friktion og varme i dækkets karkasse, som i værste fald får slidbanen til at slippe.
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Dæktype / Dimension</th>
                <th className="py-2.5 pr-4 font-semibold">Trailer totalvægt</th>
                <th className="py-2.5 pr-4 font-semibold">Anbefalet tryk (Bar)</th>
                <th className="py-2.5 font-semibold">Anbefalet tryk (PSI)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">145/80 R13 (standard)</td>
                <td className="py-3 pr-4">500 – 750 kg (enkeltaksel)</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">2,5 – 2,8 bar</td>
                <td className="py-3 text-[#5C636B]">36 – 40 PSI</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">155/70 R13</td>
                <td className="py-3 pr-4">500 – 750 kg</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">2,5 – 3,0 bar</td>
                <td className="py-3 text-[#5C636B]">36 – 44 PSI</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">155 R13 C (forstærket)</td>
                <td className="py-3 pr-4">1.000 – 1.500 kg</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">3,8 – 4,5 bar</td>
                <td className="py-3 text-[#5C636B]">55 – 65 PSI</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">185 R14 C (tung trailer)</td>
                <td className="py-3 pr-4">2.000 – 3.500 kg (bogie)</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">4,2 – 4,5 bar</td>
                <td className="py-3 text-[#5C636B]">60 – 65 PSI</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">195/50 R13 C (lavprofil)</td>
                <td className="py-3 pr-4">1.500 – 2.700 kg</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">5,5 – 6,0 bar</td>
                <td className="py-3 text-[#5C636B]">80 – 87 PSI</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Aflæs altid det maksimale koldtryk, der er støbt ind i dæksiden (angivet som <em>MAX LOAD ... AT ... PSI COLD</em>). Dæktrykket skal altid måles på kolde dæk.
        </p>

        <h2 id="stoerrelser-og-c-daek" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Typiske dækstørrelser (145/80 R13) og C-dæk
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Den mest solgte trailerdæk-størrelse i Danmark er <strong>145/80 R13</strong> (eller 145 R13). Det findes på titusindvis af fritidstrailere.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Når du køber nye dæk til traileren, skal du sikre dig, at <strong>Load Index (LI)</strong> er tilstrækkeligt. Har du en trailer med en tilladt akseltryk på 750 kg, skal hvert dæk mindst kunne bære 375 kg (Load Index 74). Er traileren tungere, skal du vælge et <strong>C-dæk</strong> med ekstra forstærkede sidevægge.
        </p>

        <h2 id="baadtrailere-og-vinterdaek" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Bådtrailere, stilstand og vinterdæk
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Danmark er der intet generelt lovkrav om vinterdæk – hverken for biler eller trailere. Men hvis du kører til nabolande:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Sverige:</strong> Har trækbilen vinterdæk i perioden 1. dec – 31. marts ved vinterføre, <em>skal</em> traileren også have vinterdæk. Har bilen pigdæk, <em>skal</em> traileren have pigdæk (se <Link to="/trailer-i-sverige" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i Sverige</Link>).</li>
          <li><strong>Fladtrykning ved stilstand:</strong> Står traileren stille i månedsvis (f.eks. en bådtrailer over vinteren), bør dæktrykket hæves med 0,3–0,5 bar, eller hjulene aflastes på bukke for at undgå permanente flade punkter på dækkene.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om synskontrol af dæk i <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">syn af trailer</Link> og vægtregler i <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer totalvægt</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
